import { useMutation, useReactiveVar } from "@apollo/client";
import { CewpUserForm } from "components/forms";
import {
  CreateCewpUserSchema,
  getCreateCewpUserInitialValues,
} from "components/forms/cewp-user/schema";
import { Modal } from "components/layouts";
import { currentDistrictVar } from "apollo/cache/auth";
import { useFormik } from "formik";
import { FC } from "react";
import toast from "react-hot-toast";
import { wrapClick } from "utils";
import { CREATE_CEWP_USER } from "./apollo";

interface CreateCewpUserContainerProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}

const CreateCewpUserContainer: FC<CreateCewpUserContainerProps> = ({
  open,
  setOpen,
  refetch,
}) => {
  const currentDistrict = useReactiveVar(currentDistrictVar);
  const [createCEWPUser, { loading }] = useMutation(CREATE_CEWP_USER);

  const form = useFormik({
    initialValues: getCreateCewpUserInitialValues(currentDistrict ?? undefined),
    enableReinitialize: true,
    validationSchema: CreateCewpUserSchema,
    onSubmit: async (values) => {
      const districts = values.districts.filter(Boolean);
      await createCEWPUser({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          emailAddress: values.emailAddress,
          certificationNumber: values.certificationNumber,
          profileImageUrl: values.profileImageUrl || undefined,
          districts,
        },
      }).then(({ data }) => {
        if (data?.createCEWPUser?._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "CEWP User Created Successfully",
            }),
          );
          refetch?.();
          form.resetForm();
          setOpen(false);
        } else {
          toast(
            JSON.stringify({
              type: "error",
              title: "Could not create CEWP user",
            }),
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Add CEWP User"
      description="Provide the details to add a new CEWP user"
      renderActions={() => (
        <button
          type="button"
          disabled={loading}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.handleSubmit)}
        >
          {loading ? "Adding User..." : "Add User"}
        </button>
      )}
    >
      <CewpUserForm form={form} />
    </Modal>
  );
};

export default CreateCewpUserContainer;
